/**
 * Stage configuration — defines the card interface for each pipeline stage.
 * Controls: card count, required selections, context fields, mode (pick vs review).
 */

import type { StageConfigMap, StageTaskMap, FieldTagMap } from '../types/stages';

export const STAGE_CONFIG: StageConfigMap = {
  new: {
    label: 'New Word',
    mode: 'entry', // word entry form
  },

  stage1: {
    label: 'Definition & Core Identity',
    mode: 'review', // review all fields, no selection
    readOnlyFields: [
      'correct_definition',
      'definition_confidence',
      'sensitivity_flag',
      'difficulty',
      'word_themes',
      'word_vibe',
      'sources_text',
      'related_word_1',
      'related_word_1_def',
      'related_word_2',
      'related_word_2_def',
      'related_word_3',
      'related_word_3_def',
    ],
    fieldGroups: [
      {
        title: 'Identity',
        fields: ['correct_definition', 'definition_enhanced', 'definition_confidence', 'sensitivity_flag'],
      },
      {
        title: 'Context',
        fields: ['etymology', 'usage_context', 'pronunciation_tip'],
      },
      {
        title: 'Metadata',
        fields: ['difficulty', 'word_themes', 'word_vibe', 'sources_text'],
      },
      {
        title: 'SEO',
        fields: ['paa_q1', 'paa_a1', 'paa_q2', 'paa_a2', 'paa_q3', 'paa_a3'],
      },
      {
        title: 'Related Words',
        fields: ['related_word_1', 'related_word_1_def', 'related_word_2', 'related_word_2_def', 'related_word_3', 'related_word_3_def'],
      },
      {
        title: 'NLP',
        fields: ['share_card_benefit_hook', 'share_card_challenge_hook', 'winner_reframe', 'loser_reframe'],
      },
      {
        title: 'Affiliate',
        fields: ['problem_category', 'opposite_concept', 'psychological_triggers'],
      },
    ],
    generateEndpoint: '/api/generate/stage1',
  },

  stage1b: {
    label: 'Fake Definitions',
    mode: 'pick',
    cardFields: ['wrong_def_1', 'wrong_def_2', 'wrong_def_3', 'wrong_def_4', 'wrong_def_5', 'wrong_def_6'],
    selectedFields: ['selected_wrong_1', 'selected_wrong_2', 'selected_wrong_3'],
    requiredSelections: 3,
    contextFields: ['correct_definition', 'definition_enhanced'],
    generateEndpoint: '/api/generate/stage1b',
  },

  stage2: {
    label: 'Example Sentences',
    mode: 'pick',
    cardFields: ['example_sentence_1', 'example_sentence_2', 'example_sentence_3', 'example_sentence_4', 'example_sentence_5', 'example_sentence_6'],
    selectedFields: ['selected_example_1', 'selected_example_2', 'selected_example_3'],
    requiredSelections: 3,
    contextFields: ['definition_enhanced'],
    generateEndpoint: '/api/generate/stage2',
  },

  stage3: {
    label: 'How To Use',
    mode: 'pick',
    cardFields: ['how_to_use_1', 'how_to_use_2', 'how_to_use_3', 'how_to_use_4', 'how_to_use_5', 'how_to_use_6'],
    selectedFields: ['selected_how_to_use_1', 'selected_how_to_use_2', 'selected_how_to_use_3'],
    requiredSelections: 3,
    contextFields: ['definition_enhanced'],
    generateEndpoint: '/api/generate/stage3',
  },

  stage4: {
    label: 'Fun Facts',
    mode: 'pick',
    cardFields: ['fun_fact_1', 'fun_fact_2', 'fun_fact_3', 'fun_fact_4', 'fun_fact_5'],
    selectedFields: ['selected_fun_fact_1', 'selected_fun_fact_2', 'selected_fun_fact_3'],
    requiredSelections: 3,
    contextFields: ['correct_definition'],
    generateEndpoint: '/api/generate/stage4',
    allowFewerSelections: true,
  },

  stage5: {
    label: 'Audio',
    mode: 'audio',
    generateEndpoint: '/api/generate/stage5',
  },

  stage6: {
    label: 'Reframes',
    mode: 'reframes', // special 3-tier pick
    tiers: [
      {
        title: 'Newsletter Reframes',
        cardFields: ['newsletter_reframe_1', 'newsletter_reframe_2', 'newsletter_reframe_3'],
        selectedField: 'selected_newsletter_reframe',
        requiredSelections: 1,
        contextFields: ['definition_enhanced', 'selected_wrong_1', 'selected_wrong_2', 'selected_wrong_3'],
      },
      {
        title: 'Second Chance Reframes',
        cardFields: ['second_chance_reframe_1', 'second_chance_reframe_2', 'second_chance_reframe_3'],
        selectedField: 'selected_second_chance_reframe',
        requiredSelections: 1,
        contextFields: ['definition_enhanced', 'selected_wrong_1', 'selected_wrong_2', 'selected_wrong_3'],
      },
      {
        title: 'Third Chance Reframes',
        cardFields: ['third_chance_reframe_1', 'third_chance_reframe_2', 'third_chance_reframe_3'],
        selectedField: 'selected_third_chance_reframe',
        requiredSelections: 1,
        contextFields: ['definition_enhanced', 'selected_second_chance_reframe'],
      },
    ],
    generateEndpoint: '/api/generate/stage6',
  },

  // stage7 (Social Teasers) and stage9 (Related Word Social) retired
  // 2026-07-05 — social content moves to the future Social Engine.

  stage8: {
    label: 'Related Words',
    mode: 'related',
    generateEndpoint: '/api/generate/stage8',
    // Content fields per related word — defined in RelatedStage.jsx RELATED_FIELD_GROUPS
  },

  stage10: {
    label: 'Final Review',
    mode: 'final-review',
  },

  stage11: {
    label: 'Transfer',
    mode: 'transfer', // Transfer to GAMES CORE + D1 upload
  },
};

// Stage → canonical task name mapping (mirrors STAGE_TASK_MAP on the worker)
export const STAGE_TASK_MAP: StageTaskMap = {
  stage1: 'stage1_definition',
  stage1b: 'stage1b_fakes',
  stage2: 'stage2_examples',
  stage3: 'stage3_howtouse',
  stage4: 'stage4_funfacts',
  stage6: 'stage6_reframes',
  stage8: 'stage8_related',
};

// Tag auto-assignment for Learning Layer
export const FIELD_TAGS: FieldTagMap = {
  definition_enhanced: 'voice_drift',
  wrong_def: 'fake_quality',
  example_sentence: 'example_quality',
  how_to_use: 'how_to_use_quality',
  fun_fact: 'fact_quality',
  hint: 'hint_quality',
  etymology: 'etymology_quality',
  winner_reframe: 'reframe_quality',
  loser_reframe: 'reframe_quality',
  teaser: 'social_quality',
};

export function getFieldTag(fieldName: string): string {
  for (const [prefix, tag] of Object.entries(FIELD_TAGS)) {
    if (fieldName.startsWith(prefix)) return tag;
  }
  return 'general';
}
